import React, { useEffect, useMemo } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Paper,
    Chip,
    Alert,
    CircularProgress,
} from "@mui/material";
import { Search, Build } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store";
import {
    loadTools,
    setSearchQuery,
    setSelectedCategory,
    addToFavorites,
    removeFromFavorites,
    recordToolUsage,
} from "../../store/slices/toolsSlice";
import { ToolCard } from "./components/ToolCard";
import { ToolCategory } from "../../types/tools";

const categoryLabels: Record<ToolCategory | "all", string> = {
    all: "All Tools",
    customer_service: "Customer Service",
    inventory: "Inventory",
    reports: "Reports",
    analytics: "Analytics",
    utilities: "Utilities",
};

export const ToolsDashboard: React.FC = () => {
    const dispatch = useAppDispatch();
    const {
        tools,
        recentlyUsed,
        favorites,
        searchQuery,
        selectedCategory,
        isLoading,
        error,
    } = useAppSelector((state) => state.tools);

    useEffect(() => {
        dispatch(loadTools());
    }, [dispatch]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSearchQuery(event.target.value));
    };

    const handleCategoryChange = (
        _event: React.SyntheticEvent,
        newValue: ToolCategory | "all",
    ) => {
        dispatch(setSelectedCategory(newValue));
    };

    const handleToggleFavorite = (toolId: string) => {
        if (favorites.includes(toolId)) {
            dispatch(removeFromFavorites(toolId));
        } else {
            dispatch(addToFavorites(toolId));
        }
    };

    const handleLaunchTool = (toolId: string) => {
        dispatch(recordToolUsage(toolId));
    };

    const filteredTools = useMemo(() => {
        return tools.filter((tool) => {
            const matchesSearch =
                tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tool.description
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase());
            const matchesCategory =
                selectedCategory === "all" ||
                tool.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [tools, searchQuery, selectedCategory]);

    const recentTools = useMemo(() => {
        return recentlyUsed
            .map((id) => tools.find((tool) => tool.id === id))
            .filter((tool): tool is (typeof tools)[0] => tool !== undefined);
    }, [recentlyUsed, tools]);

    const favoriteTools = useMemo(() => {
        return favorites
            .map((id) => tools.find((tool) => tool.id === id))
            .filter((tool): tool is (typeof tools)[0] => tool !== undefined);
    }, [favorites, tools]);

    const activeToolsCount = tools.filter((t) => t.status === "active").length;
    const comingSoonCount = tools.filter(
        (t) => t.status === "coming_soon",
    ).length;

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "60vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Build
                        sx={{ fontSize: 40, mr: 2, color: "primary.main" }}
                    />
                    <Typography variant="h4" component="h1">
                        Tools
                    </Typography>
                </Box>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Utility tools to help you handle customer service tasks
                    efficiently
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip
                        label={`${activeToolsCount} Active`}
                        color="success"
                        size="small"
                    />
                    <Chip
                        label={`${comingSoonCount} Coming Soon`}
                        color="default"
                        size="small"
                    />
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search tools..."
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ mb: 3 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Category Tabs */}
            <Paper sx={{ mb: 4 }}>
                <Tabs
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {Object.entries(categoryLabels).map(([key, label]) => (
                        <Tab key={key} label={label} value={key} />
                    ))}
                </Tabs>
            </Paper>

            {/* Recently Used Tools */}
            {recentTools.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Recently Used
                    </Typography>
                    <Grid container spacing={3}>
                        {recentTools.map((tool) => (
                            <Grid
                                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                key={tool.id}
                            >
                                <ToolCard
                                    tool={tool}
                                    isFavorite={favorites.includes(tool.id)}
                                    onToggleFavorite={handleToggleFavorite}
                                    onLaunch={handleLaunchTool}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Favorite Tools */}
            {favoriteTools.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Favorites
                    </Typography>
                    <Grid container spacing={3}>
                        {favoriteTools.map((tool) => (
                            <Grid
                                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                key={tool.id}
                            >
                                <ToolCard
                                    tool={tool}
                                    isFavorite={true}
                                    onToggleFavorite={handleToggleFavorite}
                                    onLaunch={handleLaunchTool}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* All Tools */}
            <Box>
                <Typography variant="h6" gutterBottom>
                    {selectedCategory === "all"
                        ? "All Tools"
                        : categoryLabels[selectedCategory]}
                </Typography>
                {filteredTools.length === 0 ? (
                    <Alert severity="info">
                        No tools found matching your search criteria.
                    </Alert>
                ) : (
                    <Grid container spacing={3}>
                        {filteredTools.map((tool) => (
                            <Grid
                                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                key={tool.id}
                            >
                                <ToolCard
                                    tool={tool}
                                    isFavorite={favorites.includes(tool.id)}
                                    onToggleFavorite={handleToggleFavorite}
                                    onLaunch={handleLaunchTool}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Container>
    );
};
